/**
 * useProfileInsights — fetches the v3 projection-insight + team surfaces for the
 * full profile (mirrors the desktop useProjectionInsights + PlayerAdvancedAnalytics
 * fetches). Status-safe: each surface returns null when its endpoint reports an
 * empty status, and the consuming block falls back to honest-empty.
 *
 *   il          → /analytics/players/{id}/il-risk            (all players)
 *   anomaly     → /analytics/players/{id}/gap-anomaly/{t}     (all players)
 *   comparables → /analytics/players/{id}/profile-comps       (comparables
 *                 overhaul Gate 2a: the consumer comps card is the physical-
 *                 profile kNN read, NOT the gap-pattern endpoint — that stays
 *                 live for a future labeled power view. covered:false shapes
 *                 {reason:'pitcher'|'insufficient_sample'|'not_current'} flow
 *                 through so SummaryTab renders the labeled absent states.)
 *   impact      → /leagues/{lg}/teams/{owningTeam}/players/{id}/fantasy-impact
 *                 (owned players only — needs the owning team_id off the player)
 *   fit         → /leagues/{lg}/my-team → /leagues/{lg}/optimizer/
 *                 personalized-value/{id}?team_id=<mine> — roster-fit from the
 *                 VIEWER's perspective (DD-FIXES Bug 5: the viewer's team is
 *                 resolved server-side from the auth user via getMyTeam; no
 *                 page needs to thread a team_id prop). C44 correction
 *                 (2026-07-14): SummaryTab renders this under its honest
 *                 'Roster Fit' label — never under 'Fantasy Value'.
 *
 * Pitchers included (DD-FIXES Bug 4 fix B): the gap-vector archive covers
 * pitcher targets (K/BB/ER/HR_allowed) for every season — the old
 * "hitter-only" skip was a false premise that rendered fake empty states.
 * The display target 'ERA' resolves to the archived 'ER' model target
 * server-side (_ARCHIVE_TARGET_MAP, fix C — single resolution site).
 */
import { useState, useEffect } from 'react';
import { playersAPI, leaguesAPI } from '../../../../../../services/apiService';

export const useProfileInsights = (playerId, { isPitcher, leagueId, owningTeamId, isOwned } = {}) => {
  const target = isPitcher ? 'ERA' : 'HR';   // pitcher-aware default (matches desktop)
  const [il, setIl] = useState(null);
  const [anomaly, setAnomaly] = useState(null);
  const [comparables, setComparables] = useState(null);
  const [series, setSeries] = useState(null);
  const [impact, setImpact] = useState(null);
  const [fit, setFit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!playerId) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    setIl(null); setAnomaly(null); setComparables(null); setSeries(null); setImpact(null); setFit(null);

    const jobs = [
      playersAPI.getPlayerIlRisk(playerId).then((d) => { if (alive) setIl(d); }).catch(() => {}),
      // ALL players — pitcher targets are archived too (Bug 4 fix B; 'ERA'
      // aliases to the archived 'ER' server-side, fix C).
      playersAPI.getPlayerGapAnomaly(playerId, target).then((d) => { if (alive) setAnomaly(d); }).catch(() => {}),
      // Profile comps (Gate 2a) — replaces the gap-pattern comparables leg only;
      // il-risk + gap-anomaly above are untouched.
      playersAPI.getPlayerProfileComps(playerId).then((d) => { if (alive) setComparables(d); }).catch(() => {}),
      // Projection-history sparkline (DD-PARITY §14.0) — the SAME series endpoint
      // desktop uses; mobile was previously not fetching it at all.
      playersAPI.getPlayerProjectionSeries(playerId, target, isPitcher ? 'pitcher' : 'hitter')
        .then((d) => { if (alive) setSeries(d); }).catch(() => {}),
    ];
    // Team contribution — owned players only (needs the owning team_id).
    if (isOwned && leagueId && owningTeamId) {
      jobs.push(
        playersAPI.getPlayerFantasyImpact(leagueId, owningTeamId, playerId)
          .then((d) => { if (alive) setImpact(d || null); }).catch(() => {})
      );
    }
    // Roster-fit from the VIEWER's perspective (Bug 5): resolve my team, then
    // personalized value. Status-safe like the other surfaces — any failure
    // (no team in this league, endpoint error) leaves fit null → honest empty.
    if (leagueId) {
      jobs.push(
        leaguesAPI.getMyTeam(leagueId)
          .then((t) => (t?.team_id
            ? leaguesAPI.getPersonalizedValue(leagueId, playerId, t.team_id)
            : null))
          .then((d) => { if (alive) setFit(d && d.grade ? d : null); })
          .catch(() => {})
      );
    }

    Promise.allSettled(jobs).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [playerId, isPitcher, leagueId, owningTeamId, isOwned, target]);

  return { il, anomaly, comparables, series, impact, fit, loading };
};

export default useProfileInsights;
